/**
 * Determines the type of video to render for the BlockVideo slice.
 *
 * Resolution order:
 *   1. youtube_id present → YouTube
 *   2. video_url matches vimeo.com → Vimeo
 *   3. video_url matches loom.com/share → Loom
 *   4. video_url ends with .mp4 / .webm / .ogg → Native MP4
 *   5. video_url non-empty but unrecognised → Unsupported
 *   6. nothing → None
 */

export type VideoType =
  | { kind: "youtube"; id: string }
  | { kind: "vimeo"; url: string }
  | { kind: "loom"; url: string }
  | { kind: "mp4"; url: string }
  | { kind: "unsupported"; url: string }
  | { kind: "none" };

const VIMEO_REGEX = /(?:vimeo\.com\/(?:video\/)?)(\d+)/;
const LOOM_REGEX = /loom\.com\/share\/([a-zA-Z0-9]+)/;
const NATIVE_REGEX = /\.(mp4|webm|ogg)(\?.*)?$/i;
const YOUTUBE_REGEX = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;

export function getVideoType(
  youtubeId: string | null | undefined,
  videoUrl: string | null | undefined
): VideoType {
  // 1. YouTube wins if the legacy field is filled
  if (youtubeId) {
    return { kind: "youtube", id: youtubeId };
  }

  const url = videoUrl?.trim();

  if (!url) {
    return { kind: "none" };
  }

  // 1.5 YouTube URL in the new field
  const ytId = getYoutubeId(url);
  if (ytId) {
    return { kind: "youtube", id: ytId };
  }

  // 2. Vimeo
  if (VIMEO_REGEX.test(url)) {
    return { kind: "vimeo", url };
  }

  // 3. Loom
  if (LOOM_REGEX.test(url)) {
    return { kind: "loom", url };
  }

  // 4. Native video file
  if (NATIVE_REGEX.test(url)) {
    return { kind: "mp4", url };
  }

  // 5. Non-empty but unrecognised
  return { kind: "unsupported", url };
}

/** Extracts a YouTube video ID from a YouTube URL. */
export function getYoutubeId(url: string): string | null {
  const match = url.match(YOUTUBE_REGEX);
  return match ? match[1] : null;
}

/** Extracts a Vimeo video ID from a Vimeo URL. */
export function getVimeoId(url: string): string | null {
  const match = url.match(VIMEO_REGEX);
  return match ? match[1] : null;
}

/** Extracts a Loom video ID from a Loom share URL. */
export function getLoomId(url: string): string | null {
  const match = url.match(LOOM_REGEX);
  return match ? match[1] : null;
}

/** 
 * Extracts query parameters and privacy hashes from a Vimeo URL.
 * Returns a formatted string of parameters starting with an ampersand 
 * (e.g. "&app_id=123&h=abc") or an empty string if none are found. 
 */
export function getVimeoParams(url: string, vimeoId: string): string {
  let params = "";
  try {
    const rawUrl = url.startsWith("http") ? url : `https://${url}`;
    const parsedUrl = new URL(rawUrl);
    
    parsedUrl.searchParams.forEach((value, key) => {
      // Preserve external params but avoid overwriting our default embed settings
      if (!["dnt", "title", "byline", "portrait", "autoplay"].includes(key)) {
        params += `&${key}=${value}`;
      }
    });
    
    // If they pasted an unlisted URL like vimeo.com/123456/abcdef, extract the hash
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    if (pathSegments.length >= 2 && !params.includes('&h=')) {
      const possibleHash = pathSegments[pathSegments.length - 1];
      if (possibleHash !== vimeoId && /^[a-zA-Z0-9]+$/.test(possibleHash)) {
        params += `&h=${possibleHash}`;
      }
    }
  } catch (e) {
    // Ignore URL parsing errors and return empty string
  }
  return params;
}
